import Validator from 'validatorjs';
import { useCallback, useEffect, useRef, useState } from "react";

const useValidator = (rules, initialValues, customErrorMessages, config = {}) => {
  const [values, setValues] = useState({});
  const [isValid, setIsValid] = useState(false);
  const [errors, setErrors] = useState(undefined);

  const onChangeRef = useRef(null);
  const prevStateRef = useRef({});
  const prevErrorsRef = useRef();
  const prevFieldsRef = useRef({});

  const { onBlurValidation = false, onInputChangeValidation = false } = config;

  useEffect(() => {
    if (initialValues) {
      syncValues(initialValues)
    }

    return () => {
      reset();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = useCallback((values) => {
    reset();

    syncValues(values)
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sync = useCallback((value) => {
    syncValues(value)
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = useCallback((onReset) => {
    prevStateRef.current = {};
    prevErrorsRef.current = undefined;
    prevFieldsRef.current = {};
    onChangeRef.current = undefined;

    onReset && onReset();

    setValues({});
    setIsValid(false);
    setErrors(undefined)
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const validate = useCallback((values) => {
    const validator = new Validator(values, rules, customErrorMessages)

    const errors = validator.errors;

    const passes = validator.passes();

    setIsValid(passes);
    setErrors(errors);

    prevErrorsRef.current = errors;

    return passes;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOnBlur = useCallback((onBlur) => {
    if (onBlurValidation) {
      validate(prevStateRef.current);
    }

    if (onBlur) {
      onBlur();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const syncValues = (value) => {
    const newValues = {
      ...prevStateRef.current,
      ...value
    };

    setValues(newValues);

    const isValidatedAlready = !!prevErrorsRef.current

    if (onInputChangeValidation || isValidatedAlready) {
      validate(newValues);
    }

    prevStateRef.current = newValues;
  }

  const handleOnChange = (value) => {
    syncValues(value);

    onChangeRef.current && onChangeRef.current(value);

    prevFieldsRef.current = {
      ...prevFieldsRef.current,
      ...value
    }
  }

  const handleSubmit = (onSubmit) => () => {
    for (const field of Object.keys(rules)) {
      prevFieldsRef.current = {
        ...prevFieldsRef.current,
        [field]: true
      }
    }

    if (validate(prevStateRef.current))  {
      onSubmit(prevStateRef.current)
    }
  }

  const bind = (name, meta = {}) => {
    const isFieldDirty = prevFieldsRef?.current[name] ?? false;
    const validations = rules[name] ?? [];

    let isRequired = false;

    if (Array.isArray(validations)) {
      isRequired = validations.includes('required');
    } else {
      isRequired = `${validations}`.includes('required');
    }

    const props = {}

    props.onChange = handleOnChange

    const newProps = {
      name: name,
      required: isRequired,
      error: errors?.first(name),
      ...props,
      ...meta
    }

    if (meta.type !== 'file') {
      newProps.value = prevStateRef?.current[name] ?? meta.value;
      newProps.isFieldDirty = meta.type !== 'file' ? isFieldDirty : undefined;
    }

    return newProps
  }

  return {
    set,
    sync,
    bind,
    reset,
    values,
    errors,
    isValid,
    setValues,
    handleOnBlur,
    handleSubmit,
    handleOnChange,
  };
}

export {
  useValidator
};